import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, GitBranch, Zap, Shield, Code, Users, ChevronRight, Network, Linkedin } from 'lucide-react';
import SignInModal from './Auth/SignInModal';
import SignUpModal from './Auth/SignUpModal';

interface LandingPageProps {
  onGetStarted: () => void;
  showAuthButtons?: boolean;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, showAuthButtons = false }) => {
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);

  const openCalendly = () => {
    window.open('https://calendly.com/scinter-support/30min', '_blank');
  };

  return (
    <>
      <SignInModal
        isOpen={showSignIn}
        onClose={() => setShowSignIn(false)}
        onSwitchToSignUp={() => {
          setShowSignIn(false);
          setShowSignUp(true);
        }}
      />
      <SignUpModal
        isOpen={showSignUp}
        onClose={() => setShowSignUp(false)}
        onSwitchToSignIn={() => {
          setShowSignUp(false);
          setShowSignIn(true);
        }}
      />
    
    <div className="min-h-screen bg-white overflow-y-auto">
      {/* Top Navigation Bar */}
      {showAuthButtons && (
        <nav className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-b border-black/10 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            {/* Logo - Left */}
            <div className="flex items-center space-x-2">
              {/* ScInter Logo - Two Interlocking Circles */}
              <svg className="h-7 w-7" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                {/* Left circle */}
                <circle cx="35" cy="50" r="22" fill="none" stroke="#1f2937" strokeWidth="6"/>
                {/* Right circle */}
                <circle cx="65" cy="50" r="22" fill="none" stroke="#1f2937" strokeWidth="6"/>
              </svg>
              <span className="text-xl font-semibold text-black tracking-tight">
                ScInter
              </span>
            </div>
            
            {/* Navigation Links - Center */}
            <div className="hidden md:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
              <a href="#about" className="text-gray-700 hover:text-black text-base font-semibold transition-colors">
                About Us
              </a>
              <a href="#operation" className="text-gray-700 hover:text-black text-base font-semibold transition-colors">
                Operation
              </a>
              <a href="#capabilities" className="text-gray-700 hover:text-black text-base font-semibold transition-colors">
                Capabilities
              </a>
            </div>
            
            {/* Auth Buttons - Right */}
            <div>
              <button
                onClick={() => setShowSignIn(true)}
                className="px-5 py-2 text-black hover:text-gray-700 border border-black/20 hover:border-black/40 rounded-lg transition-all text-xs font-medium"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowSignUp(true)}
                className="px-5 py-2 bg-black hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-all"
              >
                Sign Up
              </button>
            </div>
          </div>
        </nav>
      )}
      
      {/* Hero Section */}
      <section className={`relative min-h-screen flex items-center justify-center px-6 ${showAuthButtons ? 'pt-24' : 'py-20'}`}>
        {/* Animated Knowledge Graph Background */}
        <div className="absolute inset-0 overflow-hidden opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1000 1000">
            {/* Animated connections */}
            <g className="animate-pulse">
              <line x1="100" y1="100" x2="300" y2="200" stroke="black" strokeWidth="1" opacity="0.3" />
              <line x1="300" y1="200" x2="500" y2="150" stroke="black" strokeWidth="1" opacity="0.3" />
              <line x1="500" y1="150" x2="700" y2="250" stroke="black" strokeWidth="1" opacity="0.3" />
              <line x1="700" y1="250" x2="900" y2="200" stroke="black" strokeWidth="1" opacity="0.3" />
              <line x1="200" y1="400" x2="400" y2="450" stroke="black" strokeWidth="1" opacity="0.3" />
              <line x1="400" y1="450" x2="600" y2="400" stroke="black" strokeWidth="1" opacity="0.3" />
              <line x1="600" y1="400" x2="800" y2="500" stroke="black" strokeWidth="1" opacity="0.3" />
              <line x1="300" y1="700" x2="500" y2="750" stroke="black" strokeWidth="1" opacity="0.3" />
              <line x1="500" y1="750" x2="700" y2="700" stroke="black" strokeWidth="1" opacity="0.3" />
            </g>
            {/* Animated nodes */}
            <circle cx="100" cy="100" r="6" fill="black" className="animate-pulse" />
            <circle cx="300" cy="200" r="8" fill="black" className="animate-pulse animation-delay-2000" />
            <circle cx="500" cy="150" r="10" fill="black" className="animate-pulse" />
            <circle cx="700" cy="250" r="8" fill="black" className="animate-pulse animation-delay-2000" />
            <circle cx="900" cy="200" r="6" fill="black" className="animate-pulse" />
            <circle cx="200" cy="400" r="8" fill="black" className="animate-pulse animation-delay-2000" />
            <circle cx="400" cy="450" r="10" fill="black" className="animate-pulse" />
            <circle cx="600" cy="400" r="8" fill="black" className="animate-pulse animation-delay-2000" />
            <circle cx="800" cy="500" r="6" fill="black" className="animate-pulse" />
            <circle cx="300" cy="700" r="8" fill="black" className="animate-pulse animation-delay-2000" />
            <circle cx="500" cy="750" r="10" fill="black" className="animate-pulse" />
            <circle cx="700" cy="700" r="8" fill="black" className="animate-pulse animation-delay-2000" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-left"
            >
              {/* Logo and Title */}
              <div className="flex items-center space-x-3 mb-6">
                <div className="relative">
                  {/* ScInter Logo - Two Interlocking Circles */}
                  <svg className="h-12 w-12 md:h-14 md:w-14" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    {/* Left circle */}
                    <circle cx="35" cy="50" r="22" fill="none" stroke="#1f2937" strokeWidth="6"/>
                    {/* Right circle */}
                    <circle cx="65" cy="50" r="22" fill="none" stroke="#1f2937" strokeWidth="6"/>
                  </svg>
                </div>
                <h1 className="text-4xl md:text-6xl font-bold text-black tracking-tight">
                  ScInter
                </h1>
              </div>

              {/* Subtitle */}
              <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4">
                Intelligent R&D Platform
              </h2>

              {/* Description */}
              <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                Transform your research and development with AI-powered <span className="font-semibold text-purple-600">Knowledge Graphs</span>, intelligent document processing, and seamless collaboration tools.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3">
                {showAuthButtons ? (
                  <>
                    <motion.button
                      onClick={openCalendly}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="group px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm font-medium rounded-lg shadow-lg flex items-center space-x-2 transition-all"
                    >
                      <span>Book a Demo</span>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                    <motion.button
                      onClick={() => setShowSignIn(true)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 border-2 border-purple-600 text-purple-600 hover:bg-purple-50 text-sm font-medium rounded-lg transition-all"
                    >
                      Sign In
                    </motion.button>
                  </>
                ) : (
                  <>
                    <motion.button
                      onClick={onGetStarted}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="group px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm font-medium rounded-lg shadow-lg flex items-center space-x-2 transition-all"
                    >
                      <span>Get Started</span>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                    <motion.a
                      href="#features"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 border-2 border-purple-600 text-purple-600 hover:bg-purple-50 text-sm font-medium rounded-lg transition-all"
                    >
                      Learn More
                    </motion.a>
                  </>
                )}
              </div>
            </motion.div>

            {/* Right Column - Interactive Knowledge Graph */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative w-full h-[500px] bg-gradient-to-br from-purple-50 via-white to-blue-50 rounded-2xl border border-purple-100 shadow-2xl overflow-hidden p-8">
                {/* Clean Knowledge Graph with Well-Designed Nodes */}
                <svg className="w-full h-full" viewBox="0 0 600 500">
                  <defs>
                    {/* Clean gradients */}
                    <linearGradient id="edgeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#8b5cf6', stopOpacity: 0.4}} />
                      <stop offset="100%" style={{stopColor: '#3b82f6', stopOpacity: 0.4}} />
                    </linearGradient>
                    
                    <radialGradient id="nodeGrad">
                      <stop offset="0%" style={{stopColor: '#ffffff', stopOpacity: 1}} />
                      <stop offset="60%" style={{stopColor: '#f9fafb', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#e5e7eb', stopOpacity: 1}} />
                    </radialGradient>
                    
                    <radialGradient id="centerGrad">
                      <stop offset="0%" style={{stopColor: '#8b5cf6', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#6d28d9', stopOpacity: 1}} />
                    </radialGradient>
                    
                    {/* Shadow filter */}
                    <filter id="shadow">
                      <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.15"/>
                    </filter>
                  </defs>
                  
                  {/* Clean Edges */}
                  <g opacity="0.6">
                    {/* Connections to center */}
                    <line x1="150" y1="150" x2="300" y2="250" stroke="url(#edgeGrad)" strokeWidth="2"/>
                    <line x1="450" y1="150" x2="300" y2="250" stroke="url(#edgeGrad)" strokeWidth="2"/>
                    <line x1="150" y1="350" x2="300" y2="250" stroke="url(#edgeGrad)" strokeWidth="2"/>
                    <line x1="450" y1="350" x2="300" y2="250" stroke="url(#edgeGrad)" strokeWidth="2"/>
                    <line x1="300" y1="100" x2="300" y2="250" stroke="url(#edgeGrad)" strokeWidth="2"/>
                    <line x1="300" y1="400" x2="300" y2="250" stroke="url(#edgeGrad)" strokeWidth="2"/>
                  </g>
                  
                  {/* Well-Designed Nodes - No Icons, Text Only */}
                  
                  {/* Center Node - ScInter Logo */}
                  <g filter="url(#shadow)">
                    <circle cx="300" cy="250" r="55" fill="url(#centerGrad)"/>
                    <circle cx="300" cy="250" r="48" fill="white" opacity="0.2"/>
                    {/* ScInter Logo - Two Interlocking Circles */}
                    <circle cx="290" cy="250" r="12" fill="none" stroke="white" strokeWidth="3"/>
                    <circle cx="310" cy="250" r="12" fill="none" stroke="white" strokeWidth="3"/>
                  </g>
                  
                  {/* Top Node */}
                  <g filter="url(#shadow)">
                    <circle cx="300" cy="100" r="42" fill="url(#nodeGrad)"/>
                    <circle cx="300" cy="100" r="38" fill="white"/>
                    <text x="300" y="105" textAnchor="middle" fill="#4b5563" fontSize="11" fontWeight="600">Research</text>
                  </g>
                  
                  {/* Top Left Node */}
                  <g filter="url(#shadow)">
                    <circle cx="150" cy="150" r="42" fill="url(#nodeGrad)"/>
                    <circle cx="150" cy="150" r="38" fill="white"/>
                    <text x="150" y="155" textAnchor="middle" fill="#4b5563" fontSize="11" fontWeight="600">Analytics</text>
                  </g>
                  
                  {/* Top Right Node */}
                  <g filter="url(#shadow)">
                    <circle cx="450" cy="150" r="42" fill="url(#nodeGrad)"/>
                    <circle cx="450" cy="150" r="38" fill="white"/>
                    <text x="450" y="155" textAnchor="middle" fill="#4b5563" fontSize="11" fontWeight="600">Processing</text>
                  </g>
                  
                  {/* Bottom Left Node */}
                  <g filter="url(#shadow)">
                    <circle cx="150" cy="350" r="42" fill="url(#nodeGrad)"/>
                    <circle cx="150" cy="350" r="38" fill="white"/>
                    <text x="150" y="355" textAnchor="middle" fill="#4b5563" fontSize="11" fontWeight="600">Insights</text>
                  </g>
                  
                  {/* Bottom Right Node */}
                  <g filter="url(#shadow)">
                    <circle cx="450" cy="350" r="42" fill="url(#nodeGrad)"/>
                    <circle cx="450" cy="350" r="38" fill="white"/>
                    <text x="450" y="355" textAnchor="middle" fill="#4b5563" fontSize="11" fontWeight="600">Context</text>
                  </g>
                  
                  {/* Bottom Node */}
                  <g filter="url(#shadow)">
                    <circle cx="300" cy="400" r="42" fill="url(#nodeGrad)"/>
                    <circle cx="300" cy="400" r="38" fill="white"/>
                    <text x="300" y="405" textAnchor="middle" fill="#4b5563" fontSize="11" fontWeight="600">Discovery</text>
                  </g>
                  
                  {/* Title */}
                  <text x="300" y="35" textAnchor="middle" fill="#1f2937" fontSize="16" fontWeight="700">Knowledge Graph</text>
                  <text x="300" y="485" textAnchor="middle" fill="#6b7280" fontSize="11" fontWeight="500">AI-Powered Connections</text>
                </svg>
                
                {/* Floating badges */}
                <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-purple-200 rounded-full text-xs font-medium text-purple-700 shadow-sm flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Live Graph</span>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full text-xs font-semibold text-white shadow-md">
                  Interactive KG
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </section>

      {/* Why ScInter Section */}
      <section id="about" className="py-20 px-6 bg-black border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why ScInter?
            </h2>
            <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              Siloed infrastructures, transient knowledge, and recurring failed experiments significantly hinder the productivity and continuity of modern R&D operations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Buried Insights',
                description: 'Critical insights are often buried in disconnected systems—emails, ELNs, lab notebooks, and document repositories.',
                icon: '🔍'
              },
              {
                title: 'Repeated Failures',
                description: 'Manual reviews and fragmented data sources result in repeated failures, delayed progress, and avoidable resource expenditure.',
                icon: '🔄'
              },
              {
                title: 'Knowledge Loss',
                description: 'The departure of key personnel frequently leads to irreversible loss of expertise and disrupted research pipelines.',
                icon: '💡'
              }
            ].map((problem, index) => (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/5 border border-white/20 rounded-lg p-5 hover:border-white/30 hover:bg-white/10 transition-all"
                >
                  <div className="text-4xl mb-3">{problem.icon}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{problem.title}</h3>
                  <p className="text-sm text-gray-300 leading-relaxed">{problem.description}</p>
                </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 bg-white/5 border border-white/20 rounded-lg p-5 text-center"
          >
            <p className="text-base md:text-lg text-gray-300 leading-relaxed">
              ScInter addresses these challenges by serving as the <span className="font-semibold text-white">intelligent, unifying layer</span> for scientific organizations—a living, continuously evolving brain that <span className="font-semibold text-white">connects, preserves, and activates</span> your institutional knowledge.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How ScInter Operates */}
      <section id="operation" className="py-16 px-6 bg-white border-t border-black/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-black mb-3">
              How ScInter Operates
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-3xl mx-auto">
              The intelligent, unifying layer that connects, preserves, and activates your institutional knowledge
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Connect Everything',
                description: 'Seamlessly ingest scientific documents, experiment data, code, and discussions without disrupting your existing workflow.',
                icon: GitBranch,
                color: 'from-blue-500 to-cyan-500'
              },
              {
                step: '2',
                title: 'AI-Powered Intelligence',
                description: 'Our core knowledge graph automatically links ideas, data, literature, and people revealing hidden patterns as your work evolves.',
                icon: Brain,
                color: 'from-purple-500 to-pink-500'
              },
              {
                step: '3',
                title: 'Accelerate Discovery',
                description: 'Search, trace, and build on prior knowledge instantly. Ask questions and get contextual answers with full traceability.',
                icon: Zap,
                color: 'from-pink-500 to-red-500'
              }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="relative"
                >
                  <div className="bg-white border border-black/10 rounded-xl p-6 h-full hover:border-black/20 hover:shadow-md transition-all">
                    <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-3xl font-semibold text-black mb-3">{step.step}</div>
                            <h3 className="text-xl font-semibold text-black mb-3">{step.title}</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Powerful Capabilities */}
      <section id="capabilities" className="py-16 px-6 bg-gray-50 border-t border-black/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-black mb-3">
              Powerful Capabilities
            </h2>
            <p className="text-gray-600 text-base">
              Everything you need to transform your R&D operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Central Knowledge Graph',
                description: 'All documents, data, and code connected in real-time, creating a living map of your organization\'s knowledge.',
                icon: GitBranch,
                color: 'blue'
              },
              {
                title: 'Modular & Extensible',
                description: 'Start with literature review or experiment tracking add modules as you grow. Scales with your needs.',
                icon: Code,
                color: 'purple'
              },
              {
                title: 'Works With Your Tools',
                description: 'Native integration with MATLAB, Python, LabView, Jupyter, GitHub, Slack, and more. No workflow disruption.',
                icon: Zap,
                color: 'pink'
              },
              {
                title: 'AI That Learns',
                description: 'Every project and document makes the system smarter, continuously improving recommendations and insights.',
                icon: Brain,
                color: 'cyan'
              },
              {
                title: 'Complete Traceability',
                description: 'Every decision and result is recorded and hyperlinked from initial idea to final outcome. Perfect for compliance.',
                icon: Shield,
                color: 'green'
              },
              {
                title: 'Smart Experimentation',
                description: 'AI-powered experiment design, automated data analysis, and intelligent failure prediction to accelerate discovery.',
                icon: Users,
                color: 'indigo'
              }
            ].map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={capability.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white border border-black/10 rounded-lg p-5 hover:border-black/20 hover:shadow-md transition-all cursor-pointer"
                >
                  <Icon className="w-8 h-8 text-black mb-3" />
                        <h3 className="text-lg font-semibold text-black mb-2">{capability.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{capability.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-16 px-6 bg-white border-t border-black/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-black mb-3">
              Perfect For Your Industry
            </h2>
            <p className="text-gray-600 text-sm">
              Purpose-built for R&D-intensive and data-driven organizations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: 'Scientific Instrumentation & Advanced Manufacturing',
                description: 'Transform R&D workflows for precision instruments. Accelerate product development cycles and discover new material applications.'
              },
              {
                title: 'Semiconductors & Clean Tech',
                description: 'Drive innovation in next-generation technologies. Connect material science breakthroughs with manufacturing processes and sustainability metrics.'
              },
              {
                title: 'Research Institutions & Healthcare',
                description: 'Enable collaborative research excellence. Streamline research protocols and accelerate knowledge transfer from lab to clinic.'
              },
              {
                title: 'Biotech & Life Sciences',
                description: 'Revolutionize drug discovery and biological research with AI-powered knowledge graphs that connect molecular data, clinical trials, and research publications.'
              },
              {
                title: 'Financial Services & Enterprise',
                description: 'Leverage market intelligence and risk analysis for data-driven investment decisions and strategic competitive positioning.'
              }
            ].map((industry, index) => (
              <motion.div
                key={industry.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-black/10 rounded-lg p-6 hover:border-black/20 hover:shadow-md transition-all group"
              >
                <h3 className="text-base font-semibold text-black mb-3 transition-colors">
                  {industry.title}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gray-50 border-t border-black/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">
              Ready to Transform Your R&D?
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Join leading research organizations using ScInter to accelerate discovery and preserve institutional knowledge.
            </p>
            {showAuthButtons ? (
              <motion.button
                onClick={openCalendly}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group px-8 py-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg shadow-md inline-flex items-center space-x-2 transition-all"
              >
                <span>Book a Demo</span>
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            ) : (
              <motion.button
                onClick={onGetStarted}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group px-8 py-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg shadow-md inline-flex items-center space-x-2 transition-all"
              >
                <span>Start Your Journey</span>
                <ChevronRight className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-black/5 py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-black mb-2">
                ScInter
              </h3>
              <p className="text-gray-600 text-xs mb-2">Deep-Tech AI Platform</p>
              <p className="text-gray-500 text-xs leading-relaxed">
                Enterprise AI platform transforming data into intelligent knowledge for R&D organizations.
              </p>
            </div>
            <div>
              <h4 className="text-black text-sm font-semibold mb-2">Contact</h4>
              <p className="text-gray-600 text-xs mb-1">www.scinter.org</p>
              <p className="text-gray-600 text-xs">support@scinter.org</p>
            </div>
            <div>
              <h4 className="text-black text-sm font-semibold mb-2">Follow Us</h4>
              <a 
                href="https://www.linkedin.com/company/scinter" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
                <span className="text-sm font-medium">LinkedIn</span>
              </a>
            </div>
          </div>
          <div className="border-t border-black/5 pt-6 text-center">
            <p className="text-gray-500 text-xs">
              © 2025 SCINTER.PVT.LTD. All rights reserved. | Privacy Policy
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;

